import { ref, computed, watch, onBeforeUnmount } from "@vue/composition-api";
import axios from "@/libs/axios";
import { useToast } from "vue-toastification/composition";
import ToastificationContent
  from "@core/components/toastification/ToastificationContent.vue";
import moment from "moment-timezone";
import store from "@/store";
import router from "@/router";
import { debounce } from "lodash";
import useFilterCounter from '@/components/Filters/useFiltersCount'

export default function useList() {
  // store.dispatch("unit/getAll");
  store.dispatch("maintenance/getAll");
  store.dispatch("service_orders_status/getAll");
  store.dispatch("maintenance_template/getAll");
  store.dispatch('maintenance_crew/getAll')
  const refListTable = ref(null);
  const toast = useToast();
  const unitOptions = computed(() => store.getters["unit/getSelectedItems"]);
  const unitFilter = ref(null);
  const maintenanceOptions = computed(() => store.getters["maintenance/getSelectedItems"]);
  const maintenanceFilter = ref(null);
  const maintenanceCrewOptions = computed(() => store.getters["maintenance_crew/getSelectedItems"]);
  const maintenanceCrewFilter = ref(null);
  const statusOptions = computed(() => store.getters["service_orders_status/getSelectedItems"]);
  const statusItems = computed(() => store.getters["service_orders_status/getItems"]);
  const statusFilter = ref(null);
  const dateFilter = ref(null)
  const startDate = ref(null)
  const endDate = ref(null)
  const change = computed(() => store.getters['user/getChangeTraslate'])
  const maintenanceTemplateOptions = computed(() => store.getters["maintenance_template/getSelectedItems"]);

  store.dispatch("maintenance_template/getAll");
  const templateOptions = computed(() => store.getters["maintenance_template/getSelectedItems"]);
  const templateFilter = ref(null);

  if (router.currentRoute.params.name) unitFilter.value = parseInt(router.currentRoute.name);

  const tableColumns = [{
    label: "actions.name",
    key: "actions"
  }, {
    label: "service_orders.reference_number",
    key: "reference_number",
    sortable: true
  }, {
    label: "service_orders.company",
    key: "company.name",
    sortable: true
  },
  {
    label: "maintenance.maintenance_crew",
    key: "maintenance_crew.description",
    sortable: true
  },
  { label: "service_orders.facility", key: "unit.description", sortable: true }, {
    label: 'service_orders.attention_date',
    key: 'attention_date',
    sortable: true,
    formatter: 'formatDateAssigned'
  }, {
    label: 'service_orders.status', key: 'latest_status', sortable: false,
  }, {
    label: 'service_orders.status_change_date',
    key: 'status[0].updated_at',
    sortable: false,
    formatter: 'formatDateAssigned'
  }, {
    label: 'maintenance.template', key: 'template.name', sortable: true,
  }, {
    label: 'date.created_at',
    key: 'created_at',
    sortable: true,
    formatter: 'formatDateAssigned'
  }, {
    label: 'date.updated_at',
    key: 'updated_at',
    sortable: true,
    formatter: 'formatDateAssigned'
  },];

  const perPage = ref(10);
  const totalList = ref(0);
  const currentPage = ref(1);
  const perPageOptions = [10, 25, 50, 100];
  const searchQuery = ref("");
  const sortBy = ref("id");
  const isSortDirDesc = ref(true);
  const activeView = ref('table');
  const mapOrders = ref([]);
  const mapLoading = ref(false);
  const mapError = ref(false);
  const mapTotal = ref(0);
  const mapRevision = ref(0);
  const filters = ref({ id_unit: unitFilter.value });
  let mapRequest = 0;
  let mapController = null;

  const queryParams = () => ({
    ...filters.value,
    q: searchQuery.value,
    sortBy: sortBy.value,
    sortDesc: isSortDirDesc.value,
    id_unit: filters.value.id_unit == null || Array.isArray(filters.value.id_unit)
      ? filters.value.id_unit : [filters.value.id_unit],
  });

  const fetchMap = async () => {
    const request = ++mapRequest;
    if (mapController) mapController.abort();
    const controller = new AbortController();
    mapController = controller;
    const params = { ...queryParams(), sortBy: 'id', sortDesc: true };
    const orders = new Map();
    mapRevision.value += 1;
    mapOrders.value = [];
    mapTotal.value = 0;
    mapLoading.value = true;
    mapError.value = false;
    const fetchPage = async (page, size) => {
      const { data } = await axios.get('v1/service_orders', {
        params: { ...params, max: size, 'page[n]': page },
        timeout: 30000,
        signal: controller.signal,
        headers: { Accept: 'application/json' },
      });
      if (request !== mapRequest || controller.signal.aborted) return;
      if (!data || !Array.isArray(data.data) || data.meta?.total == null
        || !Number.isInteger(Number(data.meta.total)) || Number(data.meta.total) < 0) {
        throw Object.assign(new Error('Invalid service orders response'), { code: 'INVALID_MAP_RESPONSE' });
      }
      data.data.forEach(order => orders.set(order.id, order));
      mapOrders.value = Array.from(orders.values());
      return Number(data.meta.total);
    };
    try {
      const total = await fetchPage(1, 10);
      if (request !== mapRequest || controller.signal.aborted) return;
      mapTotal.value = total;
      if (orders.size < total) {
        const pages = Math.ceil(total / 35);
        let nextPage = 1;
        const loadPages = async () => {
          while (nextPage <= pages && request === mapRequest && !controller.signal.aborted) {
            const page = nextPage++;
            await fetchPage(page, 35);
          }
        };
        await Promise.all(Array.from({ length: Math.min(3, pages) }, () => loadPages()));
      }
      if (request === mapRequest && orders.size < total) {
        throw new Error('Incomplete service orders response');
      }
    } catch (error) {
      if (request === mapRequest) {
        controller.abort();
        mapError.value = error.code === 'INVALID_MAP_RESPONSE' ? 'invalidResponse'
          : error.code === 'ECONNABORTED' ? 'timeout' : 'error';
      }
    } finally {
      if (request === mapRequest) {
        mapLoading.value = false;
        mapController = null;
      }
    }
  };

  watch(activeView, (view) => {
    if (view === 'map') fetchMap();
    else {
      mapRequest += 1;
      if (mapController) mapController.abort();
      mapController = null;
      mapLoading.value = false;
    }
  });

  const dataMeta = computed(() => {
    const localItemsCount = refListTable.value ? refListTable.value.localItems.length : 0;
    return {
      from: perPage.value * (currentPage.value - 1) + (localItemsCount ? 1 : 0),
      to: perPage.value * (currentPage.value - 1) + localItemsCount,
      of: totalList.value,
    };
  });

  const fetchList = (ctx, callback) => {
    if (sortBy.value !== "") {
      store
        .dispatch("service_orders/getAll", {
          ...queryParams(),
          max: perPage.value,
          "page[n]": currentPage.value,
        })
        .then((response) => {
          callback(response.data);
          totalList.value = response.meta.total;
        })
        .catch(() => {
          callback([]);
          toast({
            component: ToastificationContent, props: {
              title: "Error obteniendo la información",
              icon: "AlertTriangleIcon",
              variant: "danger",
            },
          });
        });
    }
  };

  const formatDateAssigned = (value) => {
    const formattedDate = moment(value).format("DD/MM/YYYY HH:mm:ss");
    return formattedDate;
  };

  const refetchData = () => {
    if (refListTable.value) refListTable.value.refresh();
    if (activeView.value === 'map') fetchMap();
  };

  watch([currentPage, perPage], () => {
    refetchData();
  });

  const resetAndRefresh = () => {
    if (currentPage.value === 1) refetchData();
    else currentPage.value = 1;
  };

  const refreshSearch = debounce(() => {
    resetAndRefresh();
  }, 500);
  watch(searchQuery, refreshSearch);
  onBeforeUnmount(() => {
    mapRequest += 1;
    if (mapController) mapController.abort();
    mapController = null;
    refreshSearch.cancel();
  });

    const filtersCount = useFilterCounter(filters);
    const buildFilters = () => {
      filters.value = {
        id_unit: unitFilter.value,
        id_maintenance_company: maintenanceFilter.value,
        id_maintenance_crew : maintenanceCrewFilter.value,
        id_status: statusFilter.value,
        id_template: templateFilter.value,
        date_filter: dateFilter.value,
        start_date: startDate.value,
        end_date: endDate.value,
      };
      resetAndRefresh();
    }
    const clearSearchFilters = () => {
      unitFilter.value = null;
      maintenanceFilter.value = null;
      maintenanceCrewFilter.value = null;
      templateFilter.value = null;
      statusFilter.value = null;
      dateFilter.value = null;
      startDate.value = null;
      endDate.value = null;
      buildFilters();
    }

  return {
    activeView,
    mapOrders,
    mapLoading,
    mapError,
    mapTotal,
    mapRevision,
    maintenanceTemplateOptions,
    statusOptions,
    statusFilter,
    maintenanceOptions,
    maintenanceFilter,
    maintenanceCrewOptions,
    maintenanceCrewFilter,
    refListTable,
    unitOptions,
    unitFilter,
    tableColumns,
    perPage,
    totalList,
    currentPage,
    perPageOptions,
    searchQuery,
    sortBy,
    isSortDirDesc,
    dataMeta,
    fetchList,
    formatDateAssigned,
    refetchData,
    dateFilter,
    startDate,
    endDate,
    change,
    statusItems,
    clearSearchFilters,
    buildFilters,
    filtersCount,
    templateOptions,
    templateFilter,
  };
}
